<script lang="ts">
	import { fromJson } from "@bufbuild/protobuf";
	import { A, Radio } from "flowbite-svelte";
	import { getContext } from "svelte";
	import { SvelteSet } from "svelte/reactivity";
	import { BundleType } from "#gen/bundles/v1/bundle_pb.js";
	import { ContactDataSchema, type ContactData } from "#gen/bundles/v1/contact_pb.js";
	import { formattedAddress } from "#lib/address.js";
	import Avatar from "#lib/components/Avatar.svelte";
	import Checkbox from "#lib/components/Checkbox.svelte";
	import P from "#lib/components/P.svelte";
	import TogglePane from "#lib/components/TogglePane.svelte";
	import ContactDetailsForm from "#lib/components/flow/editor/content/contact/ContactDetailsForm.svelte";
	import { VCARD_FIELDS, toVCardSocialType } from "#lib/components/flow/editor/content/contact/parser.js";
	import type { ComponentEditingProps } from "#lib/components/page/ComponentProps.js";
	import EditorInset from "#lib/components/page/editing/EditorInset.svelte";
	import { keyFromString } from "#lib/dataProperties.js";
	import { formatPhoneNumber } from "#lib/phonenumber.js";
	import { type JsonObject } from "#lib/protobuf-compat.js";
	import { parseIdFromLink } from "#lib/social.js";
	import type { SuiteStore } from "#lib/stores/suite.js";
	import { booleanValue, listValue, setBooleanValue, setListValue, setStringValue, setStructValue, stringValue, structValue, unsetValue } from "#lib/struct.js";

	const suite: SuiteStore<ContactData> = getContext("suite");

	let { id, page, onChange }: ComponentEditingProps = $props();

	const data = $derived(($suite.bundleType === BundleType.CONTACT ? $suite.data?.type.value : getData(structValue($page.config, id) ?? {})) as ContactData);
	const appearance = $derived(stringValue(structValue($page.config, id), "appearance"));
	const shareDisabled = $derived(booleanValue(structValue($page.config, id), "shareDisabled"));

	export function validate(): boolean {
		return true;
	}

	function setValue(e: Event, key: string) {
		const value = (e.target as HTMLSelectElement).value;
		let config = structValue($page.config, id);
		if (!config) {
			config = {} as JsonObject;
			setStructValue($page.config, id, config);
		}
		setStringValue(config, key, value);
		$page = $page;
		onChange?.();
	}

	function onUpdate(_contact: JsonObject) {
		$page = $page;
		onChange?.();
	}

	function onPreview(contact: JsonObject) {
		const list: Record<string, string>[] = [];
		for (const key in contact) {
			list.push({ type: keyFromString(key), value: contact[key] as string });
		}
		let config = structValue($page.config, id);
		if (!config) {
			config = {} as JsonObject;
			setStructValue($page.config, id, config);
		}
		setListValue(config, "items", list);
		$page = $page;
		onChange?.();
	}

	function getData(config: JsonObject): ContactData {
		const items = (listValue(config, "items") as Record<string, string>[]) ?? [];
		return fromJson(ContactDataSchema, Object.assign({}, ...items.map(item => ({ [item.type]: item.value }))));
	}

	function getNonExportedFields(): Set<string> | undefined {
		const config = structValue($page.config, id) ?? {};
		const list = (listValue(config, "nonExportedFields") as string[]) ?? undefined;
		return list ? new SvelteSet(list) : undefined;
	}

	function toggleNonExportedField(e: Event, key: string) {
		const checked = (e.target as HTMLInputElement).checked;
		let config = structValue($page.config, id);
		if (!config) {
			config = {} as JsonObject;
			setStructValue($page.config, id, config);
		}
		let list = (listValue(config, "nonExportedFields") as string[]) ?? undefined;
		if (!list) {
			if (checked) {
				return;
			} else {
				list = [key];
			}
		} else {
			if (!checked) {
				list.push(key);
			} else {
				const index = list.indexOf(key);
				if (index >= 0) {
					list.splice(index, 1);
				}
			}
		}
		if (list.length > 0) {
			setListValue(config, "nonExportedFields", list);
		} else {
			unsetValue(config, "nonExportedFields");
		}
		$page = $page;
		onChange?.();
	}

	function toggleValue(enabled: boolean, key: string) {
		let config = structValue($page.config, id);
		if (!config) {
			config = {} as JsonObject;
			setStructValue($page.config, id, config);
		}
		if (!enabled) {
			setBooleanValue(config, key, !enabled);
		} else {
			unsetValue(config, key);
		}
		$page = $page;
		onChange?.();
	}

	function getCurrentValue(key: string): string {
		switch (key) {
			case "photo":
				return data.photo?.url ?? "";
			case "name":
				return data.fullName;
			case "title":
				return data.title;
			case "company":
				return data.company;
			case "department":
				return data.department;
			case "email":
				return data.email;
			case "mobileNumber":
				return formatPhoneNumber(data.mobileNumber);
			case "workNumber":
				return formatPhoneNumber(data.workNumber);
			case "website":
				return data.website;
			case "address":
				return formattedAddress(data.address);
			default: {
				const index = data.socialLinks?.findIndex(link => key === toVCardSocialType(link.type));
				if (index >= 0) {
					return parseIdFromLink(data.socialLinks[index]);
				}
				return "";
			}
		}
	}

	function handleSelectAll(e: Event) {
		e.preventDefault();
		let config = structValue($page.config, id);
		if (!config) {
			config = {} as JsonObject;
			setStructValue($page.config, id, config);
		}
		unsetValue(config, "nonExportedFields");
		$page = $page;
		onChange?.();
	}
</script>

<EditorInset>
	<div class="flex flex-col gap-6 px-6">
		{#if $suite.bundleType !== BundleType.CONTACT}
			<ContactDetailsForm {data} {onUpdate} {onPreview} />
		{/if}
		<TogglePane enabled={true} label="Style">
			{#snippet control()}<div></div>{/snippet}
			<div class="flex w-full gap-2">
				<Radio name="appearance" custom value="cards" group={appearance} on:change={e => setValue(e, "appearance")}>
					<div class="peer-checked:border-primary-600 flex cursor-pointer flex-col items-center justify-between rounded-lg border border-gray-200 bg-white p-5 px-6 py-4 text-gray-500 hover:bg-gray-100 hover:text-gray-600">
						<div class="w-full text-center text-sm font-medium">Links</div>
					</div>
				</Radio>
				<Radio name="appearance" custom value="icons" group={appearance} on:change={e => setValue(e, "appearance")}>
					<div class="peer-checked:border-primary-600 flex cursor-pointer flex-col items-center justify-between rounded-lg border border-gray-200 bg-white p-5 px-6 py-4 text-gray-500 hover:bg-gray-100 hover:text-gray-600">
						<div class="w-full text-center text-sm font-medium">Icons</div>
					</div>
				</Radio>
			</div>
		</TogglePane>
		<TogglePane enabled={!shareDisabled} label="Contact share" description="Allow visitors to download your virtual contact card" onchange={e => toggleValue(e, "shareDisabled")}>
			<div class="flex flex-col gap-4">
				<P size="sm">Select the information to include in your virtual contact card download. <A on:click={handleSelectAll}>Select all fields</A></P>
				{#each Object.keys(VCARD_FIELDS) as group (group)}
					{@const nonExported = getNonExportedFields()}
					{#if VCARD_FIELDS[group]?.some(field => getCurrentValue(field.key))}
						<div class="flex flex-col divide-y rounded-lg border px-4 py-2">
							{#each VCARD_FIELDS[group] as field (`${group}-${field.key}`)}
								{@const value = getCurrentValue(field.key)}
								{#if (value && group !== "social") || data.socialLinks?.findIndex(link => field.key === toVCardSocialType(link.type)) !== -1}
									<div class="flex items-start gap-4 py-4">
										<Checkbox class="pt-1" checked={!nonExported?.has(field.key)} on:change={e => toggleNonExportedField(e, field.key)} />
										<div class="truncate">
											{#if $suite.bundleType === BundleType.CONTACT}
												{#if field.key === "photo"}
													<Avatar size="md" src={getCurrentValue(field.key)} />
												{:else}
													<P whitespace="nowrap" class="flex-none truncate p-0" size="sm" title={getCurrentValue(field.key)}>{getCurrentValue(field.key)}</P>
												{/if}
											{/if}
											<P class="grow" size="xs" secondary>{field.name}</P>
										</div>
									</div>
								{/if}
							{/each}
						</div>
					{/if}
				{/each}
			</div>
		</TogglePane>
	</div>
</EditorInset>
